import { useCallback, useEffect, useMemo, useState } from 'react'
import { Calendar } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import AppDateInput from '@/app/admin/components/AppDateInput'

import TransferBillingClientsSection from '../../GestionTransferts/Reservations/TransferBillingClientsSection'
import {
  isPartnerOrAffilieUser,
} from '../../GestionTransferts/Reservations/transferBillingIds'
import PartnerPassagerClientFields from '../../GestionTransferts/Reservations/PartnerPassagerClientFields'
import { SoftButton } from '@/ui/design-system'
import TamarraBookingProgress, {
  TAMARRA_STEPS_CARTE,
  TAMARRA_STEPS_SPA,
  withoutFormalitesStep,
} from '../../shared/tamarraParity/TamarraBookingProgress'
import TamarraFormalitesStep from '../../shared/tamarraParity/TamarraFormalitesStep'
import TamarraConfirmationStep from '../../shared/tamarraParity/TamarraConfirmationStep'
import { normalizeImportantInfos } from '../../shared/ImportantInfosFields'

import {
  CARD_TYPE,
  Field,
  Modal,
  NumberStepper,
  formatPeriode,
  formatTND,
  hasDailyStock,
  StockAccessInfo,
  toDateInputValue,
  clampCarteBookableDate,
  getMinBookableDateForCarte,
  validateCarteReservationDate,
} from '../shared'
import { buildAssetUrl } from '../../../../../utils/imageUrl'

import {
  EMPTY_BILLING_DRAFT,
  buildCaReservationPayload,
  isAdminUser,
  validateCaBillingDraft,
} from './buildCaReservationPayload'
import { useCaQuotedTotal } from './useCaQuotedTotal'
import { usePartnerPassagerBilling } from '../../../../../utils/partnerPassagerBilling'
import CarteDetailsStep from './CarteDetailsStep'
import TamarraCaPaymentStep from './TamarraCaPaymentStep'
import '../../shared/tamarraParity/tamarraParity.css'

const getAccesPaxBounds = (carte) => {
  const maxFromAcces = (Number(carte?.max_adults) || 0) + (Number(carte?.max_children) || 0)
  const max = maxFromAcces > 0 ? maxFromAcces : Number(carte?.max_pax) || 99
  return { min: 1, max: Math.max(1, max) }
}

/**
 * Tamarra booking steps for standalone cartes/bien-être recherche.
 * Steps: Carte|Soin → Formalités → Voyageurs → Paiement → Confirmation
 */
const TamarraCarteBookingModal = ({ carte, open, defaults, onClose, onConfirm }) => {
  const { t } = useTranslation()
  const [step, setStep] = useState(0)
  const [draft, setDraft] = useState(EMPTY_BILLING_DRAFT)
  const [pax, setPax] = useState(1)
  const [date, setDate] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [formErrors, setFormErrors] = useState({})
  const [ack, setAck] = useState({ 1: false })
  const [ackErr, setAckErr] = useState(false)
  const [resultRef, setResultRef] = useState(null)

  const isAcces = carte?.type === CARD_TYPE.ACCES
  const isSpa = carte?.type === CARD_TYPE.BIEN_ETRE
  const dailyStock = hasDailyStock(carte)
  const perPax = carte?.pricing_type === 'per_pax'
  const needsReservationFields = !isAcces || dailyStock || perPax
  const admin = isAdminUser()
  const partnerB2b = isPartnerOrAffilieUser()

  const activeImages = useMemo(
    () => (carte?.images ?? []).filter((img) => img.active && img.url),
    [carte?.images],
  )
  const coverImage = buildAssetUrl(activeImages[0]?.url)

  const setField = useCallback((key, value) => {
    setDraft((d) => ({ ...d, [key]: value }))
  }, [])

  const applyPassagerFields = useCallback((fields) => {
    setDraft((d) => ({ ...d, ...fields }))
  }, [])

  usePartnerPassagerBilling(partnerB2b && open, applyPassagerFields)

  useEffect(() => {
    if (!open) return
    setStep(0)
    setError('')
    setFormErrors({})
    setAck({ 1: false })
    setAckErr(false)
    setResultRef(null)
    setDraft({ ...EMPTY_BILLING_DRAFT })
    if (!carte) return
    const accesBounds = getAccesPaxBounds(carte)
    const defaultPax = isAcces
      ? Math.min(Math.max(Number(defaults?.pax) || 1, accesBounds.min), accesBounds.max)
      : Math.max(carte.min_pax, Number(defaults?.pax) || carte.min_pax)
    setPax(defaultPax)
    setDate(
      isAcces
        ? clampCarteBookableDate(carte, defaults?.date || carte.start_date)
        : defaults?.date || carte.start_date,
    )
  }, [open, carte, defaults, isAcces])

  const baseTotal = useMemo(() => {
    if (!carte) return 0
    if (perPax) return carte.price * pax
    return carte.price
  }, [carte, perPax, pax])

  const { quotedTotal: total } = useCaQuotedTotal({
    open,
    carte,
    baseAmount: baseTotal,
    pax,
  })

  const paxBounds = useMemo(() => {
    if (!carte) return { min: 1, max: 1 }
    if (isAcces) return getAccesPaxBounds(carte)
    return { min: carte.min_pax, max: carte.max_pax }
  }, [carte, isAcces])

  const showDate = !isAcces || dailyStock
  const showPax = !isAcces || perPax
  const importantInfos = useMemo(
    () => normalizeImportantInfos(carte?.importantInfos || carte?.important_infos),
    [carte],
  )
  const showFormalites =
    importantInfos.consignes.length > 0 || importantInfos.formalites.length > 0
  const formalitesContent = useMemo(() => {
    if (!showFormalites) return null
    const blocks = []
    if (importantInfos.formalites.length) {
      blocks.push({
        h: t('tamarraBooking.formalites.blockDocs', {
          defaultValue: 'Documents et formalités',
        }),
        items: importantInfos.formalites,
      })
    }
    if (importantInfos.consignes.length) {
      blocks.push({
        h: isSpa
          ? t('tamarraBooking.formalites.blockTipsSpa', {
              defaultValue: 'Consignes pour ce soin',
            })
          : t('tamarraBooking.formalites.blockTipsCarte', {
              defaultValue: 'Consignes pour cette carte',
            }),
        items: importantInfos.consignes,
      })
    }
    return {
      title: isSpa
        ? t('tamarraBooking.formalites.spaTitle', {
            defaultValue: 'Avant de confirmer votre soin',
          })
        : t('tamarraBooking.formalites.carteTitle', {
            defaultValue: 'Avant de confirmer votre carte',
          }),
      body: isSpa
        ? t('tamarraBooking.formalites.spaBody', {
            defaultValue:
              'Merci de prendre connaissance des consignes et formalités.',
          })
        : t('tamarraBooking.formalites.carteBody', {
            defaultValue:
              'Vérifiez la période de validité et les formalités d’accès.',
          }),
      blocks,
      cancelRows: [],
      hasContent: true,
    }
  }, [importantInfos, isSpa, showFormalites, t])
  const progressSteps = useMemo(() => {
    const base = isSpa ? TAMARRA_STEPS_SPA : TAMARRA_STEPS_CARTE
    return showFormalites ? base : withoutFormalitesStep(base)
  }, [isSpa, showFormalites])
  const progressCurrent = showFormalites ? step : step === 0 ? 0 : Math.max(0, step - 1)
  const formalitesKind = isSpa ? 'spa' : 'carte'
  const totalLabel = formatTND(total)

  useEffect(() => {
    if (!open || showFormalites || step !== 1) return
    setStep(2)
  }, [open, showFormalites, step])

  const handleAck = (n, checked) => {
    setAck((a) => ({ ...a, [n]: checked }))
    if (checked) setAckErr(false)
  }

  const goProductNext = () => {
    setError('')
    if (needsReservationFields && showDate) {
      const dateError = validateCarteReservationDate(carte, date, t)
      if (dateError) {
        setError(dateError)
        return
      }
    }
    setStep(showFormalites ? 1 : 2)
  }

  const goFormalitesNext = () => {
    if (!ack[1]) {
      setAckErr(true)
      return
    }
    setAckErr(false)
    setStep(2)
  }

  const handleBackFromVoyageurs = () => {
    setStep(showFormalites ? 1 : 0)
  }

  const goVoyageursNext = () => {
    const errors = validateCaBillingDraft(draft, t, { skipPayment: true })
    delete errors.payment_methods
    if (Object.keys(errors).length) {
      setFormErrors(errors)
      setError(Object.values(errors)[0])
      return
    }
    setFormErrors({})
    setError('')
    setStep(3)
  }

  const handlePay = async () => {
    if (isAcces && hasDailyStock(carte)) {
      const dateError = validateCarteReservationDate(carte, date, t)
      if (dateError) {
        setError(dateError)
        return
      }
    }

    const errors = validateCaBillingDraft(draft, t)
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      setError(Object.values(errors)[0])
      return
    }

    setSubmitting(true)
    setError('')
    try {
      const payload = buildCaReservationPayload(carte, draft, { date, pax, total, isAcces })
      const created = await onConfirm(payload, { keepOpen: true })
      const ref =
        created?.reference ||
        created?.data?.reference ||
        created?.id ||
        created?.data?.id ||
        null
      setResultRef(ref != null ? String(ref) : null)
      setStep(4)
    } catch {
      // parent notifies
    } finally {
      setSubmitting(false)
    }
  }

  if (!carte) return null

  const foot = (() => {
    if (step === 0) {
      return (
        <div className="tamarra-bk-foot">
          <div className="tamarra-bk-sum">
            <span className="tamarra-label">
              {isSpa
                ? t('tamarraBooking.sum.spa', { defaultValue: 'Total soin' })
                : t('tamarraBooking.sum.carte', { defaultValue: 'Total carte' })}
            </span>
            <b>{totalLabel}</b>
          </div>
          <SoftButton type="button" variant="solid" onClick={goProductNext}>
            {t('tamarraBooking.actions.confirmReservation', {
              defaultValue: 'Confirmer cette réservation',
            })}
          </SoftButton>
        </div>
      )
    }
    if (step === 1 || step === 2) {
      return (
        <div className="tamarra-bk-foot">
          <SoftButton
            type="button"
            variant="secondary"
            onClick={step === 2 ? handleBackFromVoyageurs : () => setStep(0)}
          >
            {t('tamarraBooking.actions.back', { defaultValue: 'Retour' })}
          </SoftButton>
          <div className="tamarra-bk-sum">
            <span className="tamarra-label">{t('tamarraBooking.sum.total', { defaultValue: 'Total' })}</span>
            <b>{totalLabel}</b>
          </div>
          <SoftButton
            type="button"
            variant="solid"
            onClick={step === 1 ? goFormalitesNext : goVoyageursNext}
          >
            {step === 1
              ? t('tamarraBooking.actions.toTravelers', {
                  defaultValue: 'Continuer vers les voyageurs',
                })
              : t('tamarraBooking.actions.toPayment', {
                  defaultValue: 'Continuer vers le paiement',
                })}
          </SoftButton>
        </div>
      )
    }
    if (step === 3) {
      return (
        <div className="tamarra-bk-foot">
          <SoftButton
            type="button"
            variant="secondary"
            disabled={submitting}
            onClick={() => setStep(2)}
          >
            {t('tamarraBooking.actions.back', { defaultValue: 'Retour' })}
          </SoftButton>
          <div className="tamarra-bk-sum">
            <span className="tamarra-label">
              {t('tamarraBooking.sum.payNow', { defaultValue: 'À payer maintenant' })}
            </span>
            <b>{totalLabel}</b>
          </div>
          <SoftButton type="button" variant="solid" loading={submitting} onClick={handlePay}>
            {submitting
              ? t('cartesAcces.recherche.modal.confirm')
              : t('tamarraBooking.actions.validatePay', {
                  defaultValue: 'Valider la réservation',
                })}
          </SoftButton>
        </div>
      )
    }
    return (
      <div className="tamarra-bk-foot">
        <SoftButton type="button" variant="secondary" onClick={onClose}>
          {t('tamarraBooking.actions.close', { defaultValue: 'Retour au site' })}
        </SoftButton>
      </div>
    )
  })()

  return (
    <Modal
      open={open}
      size="2xl"
      compact
      title={t('cartesAcces.recherche.modal.title')}
      onClose={onClose}
      footer={null}
      contentClassName="tamarra-parity tamarra-booking-shell"
    >
      <TamarraBookingProgress steps={progressSteps} current={progressCurrent} />

      {step === 0 ? (
        <div className="flex flex-col gap-4">
          <div className="tamarra-sec-intro">
            <span className="tamarra-label">
              {isSpa
                ? t('tamarraBooking.spa.step0', { defaultValue: 'Étape 1 sur 4 · Soin' })
                : t('tamarraBooking.carte.step0', { defaultValue: 'Étape 1 sur 4 · Carte' })}
            </span>
            <h3>{carte.label}</h3>
            <p>
              {t('tamarraBooking.carte.tripHint', {
                defaultValue:
                  'Vérifiez l’offre, la date et le nombre de personnes, puis confirmez pour poursuivre.',
              })}
            </p>
          </div>

          <CarteDetailsStep carte={carte} />

          {needsReservationFields ? (
            <>
              <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                {coverImage ? (
                  <img
                    src={coverImage}
                    alt={carte.label}
                    className="h-12 w-16 flex-shrink-0 rounded-lg object-cover"
                  />
                ) : null}
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-800">{carte.label}</p>
                  <p className="text-xs text-gray-500">{carte.facility_label}</p>
                </div>
                <div className="ml-auto flex-shrink-0 text-right">
                  <p className="text-base font-bold text-brand-700">{totalLabel}</p>
                </div>
              </div>

              {showDate ? (
                <div className="flex items-start gap-2.5 rounded-xl border border-gray-100 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-600">
                  <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" aria-hidden />
                  <p className="m-0 leading-relaxed">
                    {t('cartesAcces.recherche.modal.validityPeriod')}{' '}
                    <span className="font-medium text-gray-800">
                      {formatPeriode(carte.start_date, carte.end_date)}
                    </span>
                  </p>
                </div>
              ) : null}

              <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
                {showDate ? (
                  <Field
                    className="w-full max-w-[11.5rem] shrink-0"
                    label={t('cartesAcces.recherche.modal.date')}
                    required
                    error={error && showDate ? error : undefined}
                  >
                    <AppDateInput
                      value={date}
                      min={
                        isAcces
                          ? getMinBookableDateForCarte(carte)
                          : toDateInputValue(carte.start_date)
                      }
                      max={toDateInputValue(carte.end_date)}
                      required
                      onChange={(value) => {
                        setDate(value)
                        setError('')
                      }}
                      compact
                    />
                  </Field>
                ) : null}
                {showPax ? (
                  <Field
                    className="shrink-0"
                    label={t('cartesAcces.recherche.modal.persons', {
                      min: paxBounds.min,
                      max: paxBounds.max,
                    })}
                  >
                    <NumberStepper
                      value={pax}
                      onChange={setPax}
                      min={paxBounds.min}
                      max={paxBounds.max}
                    />
                  </Field>
                ) : null}
              </div>

              <StockAccessInfo carte={carte} appearance="banner" />
            </>
          ) : null}
        </div>
      ) : null}

      {step === 1 && showFormalites ? (
        <TamarraFormalitesStep
          kind={formalitesKind}
          ack={ack}
          onAck={handleAck}
          error={ackErr}
          content={formalitesContent}
        />
      ) : null}

      {step === 2 ? (
        <div className="flex flex-col gap-4">
          <div className="tamarra-sec-intro">
            <span className="tamarra-label">
              {t('tamarraBooking.travelers.stepLabel', {
                defaultValue: 'Étape 3 sur 4 · Liste nominative',
              })}
            </span>
            <h3>{t('tamarraBooking.travelers.title', { defaultValue: 'Qui voyage ?' })}</h3>
            <p>
              {t('tamarraBooking.travelers.carteHint', {
                defaultValue:
                  'Indiquez le contact principal et les informations de facturation pour la confirmation.',
              })}
            </p>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-gray-800">{carte.label}</p>
              <p className="text-xs text-gray-500">{carte.facility_label}</p>
            </div>
            <span className="text-lg font-bold text-brand-700">{totalLabel}</span>
          </div>
          {admin && !partnerB2b ? (
            <TransferBillingClientsSection
              draft={draft}
              setField={setField}
              formErrors={formErrors}
              appearance="tamarra"
            />
          ) : partnerB2b ? (
            <PartnerPassagerClientFields draft={draft} setField={setField} formErrors={formErrors} />
          ) : null}
          {error && !formErrors.payment_methods ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : null}
        </div>
      ) : null}

      {step === 3 ? (
        <>
          <TamarraCaPaymentStep
            carte={carte}
            draft={draft}
            date={date}
            pax={pax}
            total={total}
            errors={formErrors}
            setField={setField}
          />
          {error && !formErrors.payment_methods ? (
            <p className="tamarra-ferr">{error}</p>
          ) : null}
        </>
      ) : null}

      {step === 4 ? (
        <TamarraConfirmationStep
          title={
            isSpa
              ? t('tamarraBooking.done.thanksSpa', {
                  defaultValue: 'Merci, votre soin est enregistré.',
                })
              : t('tamarraBooking.done.thanksCarte', {
                  defaultValue: 'Merci, votre carte est enregistrée.',
                })
          }
          reference={resultRef}
          lines={[
            [
              isSpa
                ? t('tamarraBooking.done.spa', { defaultValue: 'Soin' })
                : t('tamarraBooking.done.carte', { defaultValue: 'Carte' }),
              carte.label,
            ],
            [t('tamarraBooking.done.total', { defaultValue: 'Total' }), totalLabel],
          ]}
        />
      ) : null}

      {foot}
    </Modal>
  )
}

export default TamarraCarteBookingModal
